'use strict';

var axios = require('axios');
var pako = require('pako');

exports.AvailableTemplate = void 0;
(function (AvailableTemplate) {
    AvailableTemplate["Excel"] = "Excel";
    AvailableTemplate["Powerpoint"] = "Powerpoint";
    AvailableTemplate["Image"] = "Image";
})(exports.AvailableTemplate || (exports.AvailableTemplate = {}));

exports.ColumnType = void 0;
(function (ColumnType) {
    ColumnType[ColumnType["Text"] = 1] = "Text";
    ColumnType[ColumnType["Number"] = 2] = "Number";
    ColumnType[ColumnType["Currency"] = 3] = "Currency";
    ColumnType[ColumnType["Percentage"] = 4] = "Percentage";
    ColumnType[ColumnType["PercentagePoint"] = 5] = "PercentagePoint";
})(exports.ColumnType || (exports.ColumnType = {}));

exports.PowerpointSlideType = void 0;
(function (PowerpointSlideType) {
    PowerpointSlideType["Image"] = "image";
    PowerpointSlideType["Table"] = "table";
    PowerpointSlideType["Text"] = "text";
    PowerpointSlideType["Divider"] = "divider";
})(exports.PowerpointSlideType || (exports.PowerpointSlideType = {}));

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol */


function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

class HttpError extends Error {
    constructor(axiosError) {
        var _a, _b, _c;
        super(((_b = (_a = axiosError.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.message) || 'Unknown Http error');
        this.axiosError = axiosError;
        this.name = 'HttpError';
        this.statusCode = ((_c = axiosError.response) === null || _c === void 0 ? void 0 : _c.status) || 500;
        // Keeping the axios error accessible for further details
        this.axiosError = axiosError;
    }
}

exports.RequestCompression = void 0;
(function (RequestCompression) {
    RequestCompression[RequestCompression["None"] = 0] = "None";
    RequestCompression[RequestCompression["GZip"] = 1] = "GZip";
})(exports.RequestCompression || (exports.RequestCompression = {}));
const EXPORT_ENDPOINT = '/export';
const compressionTransformer = (data, headers) => {
    if (!headers || !data) {
        return data;
    }
    headers['Content-Encoding'] = 'gzip';
    return pako.gzip(data).buffer;
};
function getCompressionTransformFn() {
    const defaults = axios.defaults.transformRequest;
    if (defaults instanceof Array) {
        return defaults.concat(compressionTransformer);
    }
    else {
        return compressionTransformer;
    }
}
/**
 * Sends an export request to the Export API and returns the exported file content and filename.
 *
 * The request is sent as a POST to the `/export` endpoint, using the provided configuration for authentication,
 * base URL, and optional request compression (gzip). The response is expected to be an array buffer containing the file data.
 *
 * If the API responds with an error, an {@link HttpError} is thrown containing the HTTP status and message.
 *
 * @param data The export data transfer object containing export parameters and metadata.
 * @param config The API configuration, including base URL, authentication, headers, and optional compression.
 * @returns A promise resolving to an object with the exported file's content (ArrayBuffer) and filename (string).
 *
 * @throws {HttpError} If the HTTP request fails, a sanitized HttpError is thrown with status and message.
 *
 * @example
 * try {
 *   const file = await exportFile(exportDto, config);
 *   // handle file.content and file.filename
 * } catch (error) {
 *   if (error instanceof HttpError) {
 *     console.error('Export failed:', error.status, error.message);
 *   } else {
 *     throw error;
 *   }
 * }
 */
function exportFile(data, config) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        let headers = config.headers;
        if ('bearerToken' in config) {
            const authHeader = config.bearerToken.match(/Bearer/) ? config.bearerToken : `Bearer ${config.bearerToken}`;
            headers = Object.assign(Object.assign({}, headers), { Authorization: authHeader });
        }
        let axiosConfig = {
            method: 'POST',
            baseURL: config.baseUrl,
            url: EXPORT_ENDPOINT,
            headers,
            data,
            responseType: 'arraybuffer',
        };
        if (config.requestCompression === exports.RequestCompression.GZip) {
            axiosConfig = Object.assign(Object.assign({}, axiosConfig), { transformRequest: getCompressionTransformFn() });
        }
        try {
            const result = yield axios(axiosConfig);
            const disposition = result.headers['content-disposition'];
            const filename = ((_a = disposition === null || disposition === void 0 ? void 0 : disposition.match(/filename="(.*)"/)) === null || _a === void 0 ? void 0 : _a[1]) || data.name;
            return {
                content: result.data,
                filename,
            };
        }
        catch (error) {
            // axios error contains sensitive data, so we have to clean it up before logging it
            if (error.config) {
                delete error.config;
            }
            if (axios.isAxiosError(error)) {
                throw new HttpError(error);
            }
            throw error;
        }
    });
}

exports.EXPORT_ENDPOINT = EXPORT_ENDPOINT;
exports.HttpError = HttpError;
exports.exportFile = exportFile;
